import { useState } from "react";
import {
    Avatar,
    Box,
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    Typography,
} from "@mui/material";
import { ListAlt, Settings, Logout } from "@mui/icons-material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import { User } from "../../../../api/interfaces/User";
import { avatarInitials } from "../../../../utils/avatarInitials";
import { CALCULATOR_BG, PREBOARD_STORAGE_KEY, SIDEBAR_AVATAR_COVER_URL } from "../../../../utils/constants";
import { useLocation, useNavigate } from "react-router-dom";
import PinnacleDrawer from "../../../../components/PinnacleDrawer/PinnacleDrawer";
import ActivateExamCode from "./drawer/ActivateExamCode";

export interface SidebarProps {
    user: Partial<User>;
    onSuccessActivateCode?: () => void;
    variant?: "desktop" | "mobile";

}
export const Sidebar: React.FC<SidebarProps> = ({ user, onSuccessActivateCode, variant }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const [logoutDialogOpen, setLogoutDialogOpen] = useState<boolean>(false);

    const menuItems = [
        { label: "Dashboard", icon: <DashboardOutlinedIcon />, path: "/" },
        { label: "Results", icon: <ListAlt />, path: "/results" },
        { label: "Settings", icon: <Settings />, path: "/settings" },
    ];

    const handleLogout = () => {
        setLogoutDialogOpen(true);
    };

    const confirmLogout = () => {
        setLogoutDialogOpen(false);
        console.log("Logging out...");
        // Perform actual logout logic here
        // Example: clear storage and navigate to login
        localStorage.removeItem(PREBOARD_STORAGE_KEY);
        sessionStorage.removeItem(PREBOARD_STORAGE_KEY);
        navigate("/login");
    };

    const cancelLogout = () => {
        setLogoutDialogOpen(false);
    };

    const hoverStyles = {
        backgroundColor: "#EEF0FA !important",
        borderRadius: 2,
        "& .MuiListItemIcon-root": {
            color: "#5840BA",
        },
    };

    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
    }

    return (
        <>
            <Box
                sx={{
                    flexShrink: 0,
                    width: { xs: "100%", md: "auto" },
                    display: variant === "desktop" ? { xs: "none", md: "block" } : "block",
                }}
            >
                <Card
                    sx={{
                        backgroundColor: "#fff",
                        borderRadius: '8px',
                        p: variant === "desktop" ? 3 : 0,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        minHeight: "100%",
                        '--Paper-shadow': 'none !important'
                    }}
                >
                    <Box>
                        <Button
                            id="activate-code-btn"
                            variant="contained"
                            sx={{
                                backgroundColor: "#6246EA",
                                borderRadius: 2,
                                textTransform: "none",
                                mb: 5,
                                fontSize: 16,
                                fontWeight: 400,
                                color: "#FFFFFF",
                                "&:hover": { backgroundColor: "#4e35c9" },
                            }}
                            fullWidth
                            onClick={() => setIsDrawerOpen(true)}
                        >
                            Activate Exam Code
                        </Button>

                        {/* User Section */}
                        <Box
                            sx={{
                                ...(variant === "desktop" && {
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                }),
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    mb: 4,
                                }}
                            >
                                <Box
                                    component="img"
                                    src={CALCULATOR_BG}
                                    alt="cover"
                                    sx={{
                                        width: "100%",
                                        height: 100,
                                        objectFit: "cover",
                                        position: "relative",
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: "relative",
                                        mt: -4,
                                        ml: 2,
                                        mb: 1,
                                    }}
                                >
                                    <Avatar
                                        {...avatarInitials(`${user.firstName} ${user.lastName}`)}
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            fontWeight: 600,
                                            fontSize: "32px",
                                            bgcolor: "#028A4F",
                                            border: "3px solid white",
                                        }}
                                    />
                                </Box>

                                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                    {user.firstName} {user.lastName}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontWeight: 400, wordBreak: "break-all" }}
                                >
                                    {user.email}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Main Menu */}
                        <List
                            id="user-side-menu"
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                "& .MuiListItemButton-root:hover": hoverStyles,
                            }}
                        >
                            {menuItems.map(({ label, icon, path }) => {
                                const isActive =
                                    location.pathname === path ||
                                    (path !== "/" && location.pathname.startsWith(path));

                                return (
                                    <ListItem key={label} disablePadding>
                                        <ListItemButton
                                            onClick={() => navigate(path)}
                                            sx={{
                                                pl: 1,
                                                pr: 1,
                                                borderRadius: 2,
                                                ...(isActive && {
                                                    backgroundColor: "#EEF0FA",
                                                    "& .MuiListItemIcon-root": {
                                                        color: "#5840BA",
                                                    },
                                                }),
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>
                                            <Typography variant="body2" color="text.secondary">
                                                {label}
                                            </Typography>
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={handleLogout}
                                    sx={{
                                        pl: 1,
                                        pr: 1,
                                        borderRadius: 2,
                                        "&:hover": hoverStyles,
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        <Logout />
                                    </ListItemIcon>
                                    <Typography variant="body2" color="text.secondary">
                                        Log out
                                    </Typography>
                                </ListItemButton>
                            </ListItem>
                        </List>
                    </Box>

                    {/* Logout Item */}
                    {/* <Box sx={{ mt: 3 }}>
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={handleLogout}
                                sx={{
                                    pl: 1,
                                    pr: 1,
                                    borderRadius: 2,
                                    "&:hover": hoverStyles,
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 40 }}>
                                    <Logout />
                                </ListItemIcon>
                                <Typography variant="body2" color="text.secondary">
                                    Log out
                                </Typography>
                            </ListItemButton>
                        </ListItem>
                    </Box> */}
                </Card>
                <PinnacleDrawer
                    title="Activate Exam Code"
                    isDrawerOpen={isDrawerOpen}
                    onClose={() => handleDrawerClose()}
                >
                    <ActivateExamCode
                        onSuccessActivateCode={onSuccessActivateCode}
                        onClose={() => handleDrawerClose()}
                    />
                </PinnacleDrawer>
            </Box>

            {/* Logout Confirmation Dialog */}
            <Dialog
                open={logoutDialogOpen}
                onClose={cancelLogout}
                aria-labelledby="logout-dialog-title"
                aria-describedby="logout-dialog-description"
            >
                <DialogTitle id="logout-dialog-title">Confirm Logout</DialogTitle>
                <DialogContent>
                    <DialogContentText id="logout-dialog-description">
                        Are you sure you want to log out?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelLogout} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={confirmLogout} color="primary" variant="contained">
                        Log out
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
